import { autorun } from "mobx";
import { CountryWithStateModel } from "./CountryModels";
import { AddressModel } from "./AddressModel";
import {
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
} from "@mindmemobile/mm-shared-global";
import { CheckoutPaymentPageModel } from "./CheckoutPaymentPageModel";
import { CheckOutPayFields } from "../enums";
import { IAPIContactFieldsModel } from "../interfaces";
import { PromotionModel } from "./PromotionModels";

export class AddOnProductModel {
  id: string;
  name: string;
  description: string;
  url: string;
  cost: string;
  isSelected: boolean;

  constructor(data?: Partial<AddOnProductModel>) {
    this.id = data?.id || "";
    this.name = data?.name || "";
    this.description = data?.description || "";
    this.url = data?.url || "";
    this.cost = data?.cost || "";
    this.isSelected = data?.isSelected || false;
  }
}

export class OptionSectionModel {
  currentPrice: number;
  currentImageIndex: number;
  selectedVariantIndex: number;
  constructor(data?: Partial<OptionSectionModel>) {
    this.currentPrice = data?.currentPrice || 0;
    this.currentImageIndex = data?.currentImageIndex || 0;
    this.selectedVariantIndex = data?.selectedVariantIndex || 0;
  }

  validate() {
    return true;
  }
}

export class PurchasePaymentModel {
  formData: { [key: string]: string };
  acceptTermsAndConditions: boolean;
  updatesOffers: boolean;
  savePaymentMethod: boolean;
  isPromotionClicked: boolean;
  promotionCodeValue: string;
  applyDisabled: boolean;
  countryDetails: CountryWithStateModel;
  promoText: string;
  paymentMethodId: string;
  taxId: string;
  billingAddress: AddressModel;
  shippingAddress: AddressModel;
  shippingSameAsBilling: boolean;
  surchargeChecked: boolean;
  errors: InputErrorModel<PurchasePaymentModel>[];
  billingErrors: InputErrorModel<AddressModel>[];
  shippingErrors: InputErrorModel<AddressModel>[];
  paymentDetails?: CheckoutPaymentPageModel;
  promotionRelatedData: PromotionModel | null;
  constructor(data?: Partial<PurchasePaymentModel>) {
    this.formData = data?.formData || {};
    this.acceptTermsAndConditions = data?.acceptTermsAndConditions || false;
    this.countryDetails = data?.countryDetails || new CountryWithStateModel();
    this.updatesOffers = data?.updatesOffers || false;
    this.surchargeChecked = data?.surchargeChecked || false;
    this.savePaymentMethod = data?.savePaymentMethod || false;
    this.isPromotionClicked = data?.isPromotionClicked || false;
    this.promotionCodeValue = data?.promotionCodeValue || "";
    this.applyDisabled = data?.applyDisabled || false;
    this.shippingSameAsBilling = data?.shippingSameAsBilling || false;
    this.promoText = data?.promoText || "";
    this.taxId = data?.taxId || "";
    this.promotionRelatedData = data?.promotionRelatedData
      ? new PromotionModel(data.promotionRelatedData)
      : null;
    this.billingAddress = data?.billingAddress
      ? new AddressModel(data.billingAddress)
      : new AddressModel();
    this.shippingAddress = data?.shippingAddress
      ? new AddressModel(data.shippingAddress)
      : new AddressModel();
    this.paymentDetails =
      data?.paymentDetails && new CheckoutPaymentPageModel(data.paymentDetails);
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<PurchasePaymentModel>(e);
        })) ||
      [];
    this.billingErrors =
      (data &&
        data?.billingErrors?.map((e) => {
          return new InputErrorModel<AddressModel>(e);
        })) ||
      [];
    this.shippingErrors =
      (data &&
        data?.shippingErrors?.map(
          (e) => new InputErrorModel<AddressModel>(e)
        )) ||
      [];
    this.paymentMethodId = data?.paymentMethodId || "";

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof PurchasePaymentModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<PurchasePaymentModel>[] = [];
    let isBillingAddressValid = true;
    let isShippingAddressValid = true;

    this.validateFormFields(newErrors);
    if (this.paymentDetails) {
      if (this.paymentDetails.formData.isBillingAddressChecked) {
        isBillingAddressValid = this.billingAddress.validate(isAutoRun);
      }
      if (
        this.paymentDetails.formData.isShippingAddressChecked &&
        !this.shippingSameAsBilling
      ) {
        isShippingAddressValid = this.shippingAddress.validate(isAutoRun);
      }
    }
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return (
      this.errors.length === 0 &&
      isBillingAddressValid &&
      isShippingAddressValid
    );
  }

  validateFormFields(newErrors: InputErrorModel<PurchasePaymentModel>[]): void {
    const { formData } = this;

    this.paymentDetails?.formData.formFields.forEach((field) => {
      if (field.isRequired) {
        if (field.field === CheckOutPayFields.FirstLastName) {
          if (!CoreInputModelUtil.isValidString(formData["firstName"])) {
            newErrors.push(
              new InputErrorModel<PurchasePaymentModel>({
                field: "firstName" as keyof PurchasePaymentModel,
                message: "First and last name are required",
              })
            );
          }
          if (!CoreInputModelUtil.isValidString(formData["lastName"])) {
            newErrors.push(
              new InputErrorModel<PurchasePaymentModel>({
                field: "lastName" as keyof PurchasePaymentModel,
                message: "First and last name are required",
              })
            );
          }
        } else if (field.field === CheckOutPayFields.EmailAddress) {
          if (
            !CoreInputModelUtil.isValidEmail(
              formData[CheckOutPayFields.EmailAddress]
            )
          ) {
            newErrors.push(
              new InputErrorModel<PurchasePaymentModel>({
                field: "EmailAddress" as keyof PurchasePaymentModel,
                message: "Please enter a valid email address",
              })
            );
          }
        } else if (field.field === CheckOutPayFields.MobileNumber) {
          if (
            !CoreInputModelUtil.isValidPhoneNumber(
              formData[CheckOutPayFields.MobileNumber]
            )
          ) {
            newErrors.push(
              new InputErrorModel<PurchasePaymentModel>({
                field: "MobileNumber" as keyof PurchasePaymentModel,
                message: "Please enter a valid mobile number",
              })
            );
          }
        } else if (field.field === CheckOutPayFields.TaxId) {
          if (
            !CoreInputModelUtil.isValidTaxId(formData[CheckOutPayFields.TaxId])
          ) {
            newErrors.push(
              new InputErrorModel<PurchasePaymentModel>({
                field: "TaxId" as keyof PurchasePaymentModel,
                message: "Please enter the Tax ID",
              })
            );
          }
        } else if (!field.field) {
          newErrors.push(
            new InputErrorModel<PurchasePaymentModel>({
              field: field.field as keyof PurchasePaymentModel,
              message: `${field.field} is required`,
            })
          );
        }
      }
    });

    if (this.paymentDetails?.isUpdatesOffers && !this.updatesOffers) {
      newErrors.push(
        new InputErrorModel<PurchasePaymentModel>({
          field: "updatesOffers",
          message: "Please agree to receive updates and offers",
        })
      );
    }

    if (
      this.paymentDetails?.sellerTermsAndConditions === "RequireAcceptance" &&
      !this.acceptTermsAndConditions
    ) {
      newErrors.push(
        new InputErrorModel<PurchasePaymentModel>({
          field: "acceptTermsAndConditions",
          message: "Please accept terms and conditions",
        })
      );
    }

    if (
      this.paymentDetails?.allowTaxId &&
      !CoreInputModelUtil.isValidTaxId(this.taxId)
    ) {
      newErrors.push(
        new InputErrorModel<PurchasePaymentModel>({
          field: "taxId",
          message: "Please enter a valid Tax ID",
        })
      );
    }
  }

  validateAddresses(
    errors: InputErrorModel<AddressModel>[],
    addressType: "billingAddress" | "shippingAddress"
  ): InputErrorModel<AddressModel>[] {
    if (addressType === "billingAddress") {
      if (this.paymentDetails?.formData.isBillingAddressChecked) {
        if (!this.billingAddress.address1) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "address1",
              message: "Street Address 1 is required",
            })
          );
        }
        if (!this.billingAddress.city) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "city",
              message: "City is required",
            })
          );
        }
        if (!this.billingAddress.zipCode) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "zipCode",
              message: "Zip Code is required",
            })
          );
        }
        if (!this.billingAddress.state.name) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "state",
              message: "State is required",
            })
          );
        }
        if (!this.billingAddress.countryDetails?.name) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "country",
              message: "Country is required",
            })
          );
        }
      }
    } else {
      if (
        this.paymentDetails?.formData.isShippingAddressChecked &&
        !this.shippingSameAsBilling
      ) {
        if (!this.shippingAddress.address1) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "address1",
              message: "Street Address 1 is required",
            })
          );
        }
        if (!this.shippingAddress.city) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "city",
              message: "City is required",
            })
          );
        }
        if (!this.shippingAddress.zipCode) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "zipCode",
              message: "Zip Code is required",
            })
          );
        }
        if (!this.shippingAddress.state.name) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "state",
              message: "State is required",
            })
          );
        }
        if (!this.shippingAddress.countryDetails?.name) {
          errors.push(
            new InputErrorModel<AddressModel>({
              field: "country",
              message: "Country is required",
            })
          );
        }
      }
    }

    return errors;
  }

  createContactFieldsAPIModel(): IAPIContactFieldsModel[] {
    const contactFields: IAPIContactFieldsModel[] = [];
    const firstName = this.formData["firstName"] || "";
    const lastName = this.formData["lastName"] || "";

    //send with pipe so that it can be split on the server side
    const combinedName = `${firstName}|${lastName}`.trim();

    contactFields.push({
      field: "FirstLastName",
      value: combinedName,
      date: null,
      countryCode: null,
      countryId: null,
    });

    for (const [field, value] of Object.entries(this.formData)) {
      if (field !== "firstName" && field !== "lastName") {
        if (field === CheckOutPayFields.MobileNumber) {
          contactFields.push({
            field: field,
            value: value,
            date: null,
            countryCode: this.countryDetails.code.toString(),
            countryId: this.countryDetails.id,
          });
        } else {
          contactFields.push({
            field: field as keyof typeof CheckOutPayFields,
            value: value,
            date: null,
            countryCode: null,
            countryId: null,
          });
        }
      }
    }

    return contactFields;
  }
}